import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ChainsService } from 'src/app/services/chains.service';
import { ApiService } from 'src/app/services/api.service';
import { ApiUrl } from 'src/app/services/apiUrl';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-chain-form',
  templateUrl: './chain-form.component.html',
  styleUrls: ['./chain-form.component.scss']
})
export class ChainFormComponent implements OnInit {
  id: string = '';
  chainForm: any;
  chain: any = null;

  allStores: any[] = [];
  chainStores: any[] = [];
  selectedStoreId: string = '';

  allAdmins: any[] = [];
  chainManagers: any[] = [];
  selectedAdminId: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private chainsService: ChainsService,
    private apiService: ApiService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {
    this.route.params.subscribe(params => {
      this.id = params['id'] || '';
    });
  }

  ngOnInit(): void {
    this.chainForm = new FormGroup({
      chainName: new FormControl('', Validators.required),
      description: new FormControl('')
    });

    if (this.id) {
      this.loadChain();
      this.loadAllStores();
      this.loadAllAdmins();
    }
  }

  get isEditMode(): boolean {
    return !!this.id;
  }

  get buttonTitle(): string {
    return this.id ? 'Edit Chain' : 'Add Chain';
  }

  loadChain(): void {
    this.spinner.show();
    this.chainsService.getChain(this.id).subscribe({
      next: (res: any) => {
        this.chain = res.chain || res.response?.data?.chain || res.response?.data;
        if (this.chain) {
          this.chainForm.patchValue({
            chainName: this.chain.chainName,
            description: this.chain.description || ''
          });
          this.chainStores = this.chain.storeIds || [];
          this.chainManagers = this.chain.managers || [];
        }
        this.spinner.hide();
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire('Error', err.error?.response?.message || 'Failed to load chain', 'error');
      }
    });
  }

  loadAllStores(): void {
    this.apiService.getRequest(ApiUrl.storeListAdminDropdown).subscribe({
      next: (res: any) => {
        this.allStores = res.response?.data?.storesData || res.response?.data || [];
      },
      error: () => {}
    });
  }

  loadAllAdmins(): void {
    this.apiService.getRequest(ApiUrl.adminList).subscribe({
      next: (res: any) => {
        const admins = res.response?.data || [];
        this.allAdmins = admins.filter((a: any) => a.role !== 'admin');
      },
      error: () => {}
    });
  }

  get availableStores(): any[] {
    const chainStoreIds = this.chainStores.map((s: any) => s._id || s);
    return this.allStores.filter((s: any) => !chainStoreIds.includes(s._id));
  }

  get availableAdmins(): any[] {
    const managerIds = this.chainManagers.map((m: any) => m._id || m);
    return this.allAdmins.filter((a: any) => !managerIds.includes(a._id));
  }

  addStore(): void {
    if (!this.selectedStoreId) return;
    this.spinner.show();
    this.chainsService.addStoreToChain(this.id, this.selectedStoreId).subscribe({
      next: () => {
        this.toastr.success('Store added to chain');
        this.selectedStoreId = '';
        this.loadChain();
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire('Error', err.error?.response?.message || 'Failed to add store', 'error');
      }
    });
  }

  removeStore(store: any): void {
    const storeId = store._id || store;
    const storeName = store.storeName || storeId;
    Swal.fire({
      title: `Remove "${storeName}" from chain?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, remove'
    }).then(result => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.chainsService.removeStoreFromChain(this.id, storeId).subscribe({
          next: () => {
            this.toastr.success('Store removed from chain');
            this.loadChain();
          },
          error: (err: any) => {
            this.spinner.hide();
            Swal.fire('Error', err.error?.response?.message || 'Failed to remove store', 'error');
          }
        });
      }
    });
  }

  assignManager(): void {
    if (!this.selectedAdminId) return;
    this.spinner.show();
    this.chainsService.assignChainManager(this.id, this.selectedAdminId).subscribe({
      next: () => {
        this.toastr.success('Manager assigned to chain');
        this.selectedAdminId = '';
        this.loadChain();
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire('Error', err.error?.response?.message || 'Failed to assign manager', 'error');
      }
    });
  }

  removeManager(manager: any): void {
    const managerId = manager._id || manager;
    const managerName = manager.name || managerId;
    Swal.fire({
      title: `Remove "${managerName}" from chain?`,
      html: `<p>Note: removing this manager from the chain will not automatically change their role. You can update their role in the Admin section if needed.</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, remove'
    }).then(result => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.chainsService.removeChainManager(this.id, managerId).subscribe({
          next: () => {
            this.toastr.success('Manager removed from chain');
            this.loadChain();
          },
          error: (err: any) => {
            this.spinner.hide();
            Swal.fire('Error', err.error?.response?.message || 'Failed to remove manager', 'error');
          }
        });
      }
    });
  }

  onSubmit(): void {
    if (this.chainForm.invalid) {
      this.toastr.error('Please fill required fields');
      return;
    }
    const data = {
      chainName: this.chainForm.get('chainName').value,
      description: this.chainForm.get('description').value
    };
    this.spinner.show();
    if (this.id) {
      this.chainsService.updateChain(this.id, data).subscribe({
        next: (res: any) => {
          this.spinner.hide();
          this.toastr.success('Chain updated successfully');
        },
        error: (err: any) => {
          this.spinner.hide();
          Swal.fire('Error', err.error?.response?.message || 'Failed to update chain', 'error');
        }
      });
    } else {
      this.chainsService.createChain(data).subscribe({
        next: (res: any) => {
          this.spinner.hide();
          this.toastr.success('Chain created successfully');
          this.router.navigate(['/chains']);
        },
        error: (err: any) => {
          this.spinner.hide();
          Swal.fire('Error', err.error?.response?.message || 'Failed to create chain', 'error');
        }
      });
    }
  }

  onCancel(): void {
    this.router.navigate(['/chains']);
  }

  getStoreStatus(store: any): string {
    return store.status === 1 || store.status === true ? 'Active' : 'Inactive';
  }

  getLastLogin(manager: any): string {
    if (!manager.lastLoginTime) return 'Never';
    return new Date(manager.lastLoginTime).toLocaleDateString();
  }
}
